import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, History, ArrowUpRight, ArrowDownRight, Printer, Download } from 'lucide-react';
import { toast } from 'sonner';
import { openBrandedPrintWindow, PrintHelpers as H } from '../utils/brandedPrint';
import { csvCell } from '../lib/utils';

const TransactionsPage = () => {
  const { api } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [disposalTickets, setDisposalTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [formData, setFormData] = useState({
    transaction_type: 'Receive',
    item_sku: '',
    quantity: 1,
    uom: 'EA',
    from_location: '',
    to_location: '',
    lot_or_serial: '',
    reference_id: '',
    reference_kind: 'free',
    reference_doc_id: '',
    reference_doc_number: '',
    notes: '',
  });

  const fetchData = async () => {
    try {
      const [txRes, itemsRes, locRes, ooRes, dtRes] = await Promise.all([
        api().get('/transactions?limit=500'),
        api().get('/items'),
        api().get('/locations'),
        api().get('/expected-orders?status=Pending').catch(() => ({ data: [] })),
        api().get('/disposal-tickets').catch(() => ({ data: [] })),
      ]);
      setTransactions(txRes.data);
      setItems(itemsRes.data);
      setLocations(locRes.data);
      setOpenOrders(ooRes.data || []);
      setDisposalTickets(dtRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api().post('/transactions', {
        ...formData,
        quantity: parseFloat(formData.quantity),
      });
      toast.success('Transaction recorded');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const resetForm = () => {
    setFormData({
      transaction_type: 'Receive',
      item_sku: '',
      quantity: 1,
      uom: 'EA',
      from_location: '',
      to_location: '',
      lot_or_serial: '',
      reference_id: '',
      reference_kind: 'free',
      reference_doc_id: '',
      reference_doc_number: '',
      notes: '',
    });
  };

  const getItemName = (sku) => items.find(i => i.item_sku === sku)?.item_name || sku;
  const getLocationName = (id) => locations.find(l => l.id === id)?.location_name || '-';

  const transactionTypes = ['Receive', 'Transfer', 'Consume', 'BuildComplete', 'Adjust', 'Scrap', 'Return'];

  const filteredItems = transactions.filter(item => {
    const matchesSearch = item.item_sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getItemName(item.item_sku).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || item.transaction_type === filterType;
    return matchesSearch && matchesFilter;
  });

  // iter67 — branded printable transaction report
  const printTxReport = async () => {
    const cols = [
      { key: 'date', label: 'Date', fmt: (_, r) => new Date(r.transaction_date || r.created_at).toLocaleString() },
      { key: 'transaction_type', label: 'Type' },
      { key: 'item_sku', label: 'SKU', mono: true },
      { key: 'item_name', label: 'Item', fmt: (_, r) => getItemName(r.item_sku) },
      { key: 'quantity', label: 'Qty', fmt: (v) => `${v}`, mono: true },
      { key: 'uom', label: 'UoM' },
      { key: 'from_location', label: 'From' },
      { key: 'to_location', label: 'To' },
      { key: 'lot_or_serial', label: 'Lot/Serial', mono: true },
      { key: 'reference_id', label: 'Ref', mono: true },
      { key: 'matched_to', label: 'Matched', mono: true, fmt: (_, r) => r.reference_doc_number ? `${r.reference_doc_number} (${(r.reference_kind || '').replace('_', ' ')})` : '' },
      { key: 'notes', label: 'Notes' },
    ];
    const totals = filteredItems.reduce((acc, t) => {
      acc[t.transaction_type] = (acc[t.transaction_type] || 0) + 1;
      return acc;
    }, {});
    const summary = Object.entries(totals).map(([k, v]) => `<span style="margin-right:14pt;"><strong>${k}:</strong> ${v}</span>`).join('');
    await openBrandedPrintWindow({
      title: 'TRANSACTION REPORT',
      subtitle: `${filteredItems.length} transaction(s)${filterType !== 'all' ? ` · ${filterType}` : ''}${searchTerm ? ` · matching "${searchTerm}"` : ''}`,
      orientation: 'landscape',
      body: `${H.callout(summary || 'No transactions in current filter.')}${H.table(cols, filteredItems)}`,
      footerNote: 'Internal Document · Inventory Movement History',
    });
  };

  const downloadTxCsv = () => {
    const headers = ['Date', 'Type', 'SKU', 'Item', 'Qty', 'UoM', 'From', 'To', 'Lot/Serial', 'Ref', 'Matched Doc', 'Matched Kind', 'Notes', 'User'];
    const rows = filteredItems.map(t => [
      new Date(t.transaction_date || t.created_at).toISOString(),
      t.transaction_type, t.item_sku, getItemName(t.item_sku),
      t.quantity, t.uom || '', t.from_location || '', t.to_location || '',
      t.lot_or_serial || '', t.reference_id || '', t.reference_doc_number || '', t.reference_kind || '',
      (t.notes || '').replace(/[\r\n]+/g, ' '),
      t.user_name || t.created_by_name || '',
    ]);
    const csv = [headers, ...rows].map(row => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filteredItems.length} transactions`);
  };


  const getTransactionIcon = (type) => {
    if (['Receive', 'Return'].includes(type)) {
      return <ArrowDownRight className="w-4 h-4 text-green-600" />;
    }
    return <ArrowUpRight className="w-4 h-4 text-red-600" />;
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'Receive': return 'status-completed';
      case 'Return': return 'status-inprogress';
      case 'Consume': return 'status-draft';
      case 'Scrap': return 'status-obsolete';
      case 'Transfer': return 'status-released';
      default: return 'status-draft';
    }
  };

  return (
    <div className="space-y-6 animate-slideIn" data-testid="transactions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inventory Transactions</h1>
          <p className="text-slate-500 text-sm">Complete inventory movement history</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => printTxReport()} data-testid="tx-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print Report
          </Button>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadTxCsv()} data-testid="tx-csv-btn">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-transaction-btn">
              <Plus className="w-4 h-4 mr-2" />
              Record Transaction
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Record Inventory Transaction</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Transaction Type *</Label>
                  <Select value={formData.transaction_type} onValueChange={(v) => setFormData({ ...formData, transaction_type: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="tx-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {transactionTypes.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Item *</Label>
                  <Select value={formData.item_sku} onValueChange={(v) => {
                    const item = items.find(i => i.item_sku === v);
                    setFormData({ ...formData, item_sku: v, uom: item?.base_uom || 'EA' });
                  }}>
                    <SelectTrigger className="rounded-sm" data-testid="tx-item-select">
                      <SelectValue placeholder="Select item" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map(item => (
                        <SelectItem key={item.item_sku} value={item.item_sku}>
                          {item.item_sku} - {item.item_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    required
                    className="rounded-sm mono"
                    data-testid="tx-quantity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>UOM</Label>
                  <Input
                    value={formData.uom}
                    onChange={(e) => setFormData({ ...formData, uom: e.target.value })}
                    className="rounded-sm"
                    data-testid="tx-uom-input"
                  />
                </div>
              </div>
              {formData.transaction_type === 'Transfer' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From Location</Label>
                    <Select value={formData.from_location || 'none'} onValueChange={(v) => setFormData({ ...formData, from_location: v === 'none' ? '' : v })}>
                      <SelectTrigger className="rounded-sm" data-testid="tx-from-location-select">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {locations.map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>{loc.location_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>To Location</Label>
                    <Select value={formData.to_location || 'none'} onValueChange={(v) => setFormData({ ...formData, to_location: v === 'none' ? '' : v })}>
                      <SelectTrigger className="rounded-sm" data-testid="tx-to-location-select">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {locations.map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>{loc.location_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Match To (optional)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Select value={formData.reference_kind} onValueChange={(v) => setFormData({ ...formData, reference_kind: v, reference_doc_id: '', reference_doc_number: '' })}>
                    <SelectTrigger className="rounded-sm" data-testid="tx-ref-kind-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">No Link</SelectItem>
                      <SelectItem value="open_order">Open Order (PO)</SelectItem>
                      <SelectItem value="disposal_ticket">Disposal Ticket</SelectItem>
                      <SelectItem value="bol">Bill of Lading</SelectItem>
                      <SelectItem value="work_order">Work Order</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.reference_kind === 'open_order' && (
                    <Select value={formData.reference_doc_id || 'none'} onValueChange={(v) => {
                      if (v === 'none') { setFormData({ ...formData, reference_doc_id: '', reference_doc_number: '' }); return; }
                      const oo = openOrders.find(o => o.id === v);
                      setFormData({ ...formData, reference_doc_id: v, reference_doc_number: oo?.purchase_order_number || '', item_sku: oo?.item_sku || formData.item_sku });
                    }}>
                      <SelectTrigger className="rounded-sm" data-testid="tx-open-order-select"><SelectValue placeholder="Pick PO" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {openOrders.slice(0, 100).map(o => (
                          <SelectItem key={o.id} value={o.id}>{o.purchase_order_number || `EO-${o.id.slice(0, 6)}`} · {o.item_sku} · {o.expected_quantity}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {formData.reference_kind === 'disposal_ticket' && (
                    <Select value={formData.reference_doc_id || 'none'} onValueChange={(v) => {
                      if (v === 'none') { setFormData({ ...formData, reference_doc_id: '', reference_doc_number: '' }); return; }
                      const dt = disposalTickets.find(d => d.id === v);
                      setFormData({ ...formData, reference_doc_id: v, reference_doc_number: dt?.ticket_number || '' });
                    }}>
                      <SelectTrigger className="rounded-sm" data-testid="tx-disposal-ticket-select"><SelectValue placeholder="Pick DT" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {disposalTickets.slice(0, 100).map(d => (
                          <SelectItem key={d.id} value={d.id}>{d.ticket_number} · {d.status} · {d.item_sku || d.disposal_kind || ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {(formData.reference_kind === 'bol' || formData.reference_kind === 'work_order') && (
                    <Input
                      placeholder={formData.reference_kind === 'bol' ? 'BOL number (e.g., BOL-000123)' : 'Work Order number'}
                      value={formData.reference_doc_number}
                      onChange={(e) => setFormData({ ...formData, reference_doc_number: e.target.value, reference_doc_id: e.target.value })}
                      className="rounded-sm mono"
                      data-testid="tx-ref-doc-num-input"
                    />
                  )}
                </div>
                {formData.reference_doc_number && (
                  <p className="text-xs text-emerald-700 mt-1" data-testid="tx-ref-doc-confirm">Linked to: <span className="mono font-semibold">{formData.reference_doc_number}</span></p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Lot/Serial Number</Label>
                <Input
                  value={formData.lot_or_serial}
                  onChange={(e) => setFormData({ ...formData, lot_or_serial: e.target.value })}
                  placeholder="Optional"
                  className="rounded-sm mono"
                  data-testid="tx-lot-serial-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Transaction notes..."
                  className="rounded-sm"
                  data-testid="tx-notes-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" disabled={!formData.item_sku} data-testid="tx-submit-btn">
                  Record
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 rounded-sm"
                data-testid="tx-search-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[160px] rounded-sm" data-testid="tx-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {transactionTypes.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="spinner" />
            </div>
          ) : filteredItems.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead></TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Lot/Serial</TableHead>
                    <TableHead>Matched To</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item) => (
                    <TableRow key={item.id} data-testid={`tx-row-${item.id}`}>
                      <TableCell>{getTransactionIcon(item.transaction_type)}</TableCell>
                      <TableCell>
                        <span className={`status-badge ${getTransactionColor(item.transaction_type)}`}>
                          {item.transaction_type}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="mono font-medium">{item.item_sku}</p>
                          <p className="text-xs text-slate-500">{getItemName(item.item_sku)}</p>
                        </div>
                      </TableCell>
                      <TableCell className="mono font-semibold">
                        {['Receive', 'Return'].includes(item.transaction_type) ? '+' : '-'}
                        {item.quantity} {item.uom}
                      </TableCell>
                      <TableCell className="mono text-sm">{item.lot_or_serial || '-'}</TableCell>
                      <TableCell className="text-xs">
                        {item.reference_doc_number ? (
                          <div data-testid={`tx-matched-${item.id}`}>
                            <span className="mono font-semibold text-emerald-700">{item.reference_doc_number}</span>
                            <span className="block text-[10px] uppercase text-slate-400">{(item.reference_kind || '').replace('_', ' ')}</span>
                          </div>
                        ) : item.reference_id ? (
                          <span className="mono text-slate-500">{item.reference_id}</span>
                        ) : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {new Date(item.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-slate-500 max-w-xs truncate">{item.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <History className="w-12 h-12 mb-3 text-slate-300" />
              <p>No transactions found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TransactionsPage;
